import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  ComposedChart, Area, Line, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw, Download, Search, ChevronUp, ChevronDown,
} from 'lucide-react';
import { apiService, eventsApi, adminApi, externalApiKeyApi } from '../../services/api';
import { AppUser, IEvent, ExternalApiKeyView } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';

// ── Currency helpers ──────────────────────────────────────────────────────────

const USD_TO_INR = 83.5;

function inrFromUsd(usd: number): string {
  const inr = usd * USD_TO_INR;
  if (inr === 0) return '₹0.00';
  if (inr < 0.001) return '₹' + inr.toFixed(5);
  if (inr < 0.01) return '₹' + inr.toFixed(4);
  if (inr < 1) return '₹' + inr.toFixed(3);
  if (inr < 100) return '₹' + inr.toFixed(2);
  return '₹' + inr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function usdStr(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.00001) return '$' + usd.toFixed(6);
  if (usd < 0.001) return '$' + usd.toFixed(5);
  if (usd < 0.01) return '$' + usd.toFixed(4);
  if (usd < 1) return '$' + usd.toFixed(4);
  if (usd < 100) return '$' + usd.toFixed(2);
  return '$' + usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function inrAxisTick(v: number): string {
  const inr = v * USD_TO_INR;
  if (inr === 0) return '₹0';
  if (inr < 0.01) return '₹' + inr.toFixed(3);
  if (inr < 1) return '₹' + inr.toFixed(2);
  return '₹' + inr.toFixed(1);
}

function formatTokens(val: number): string {
  if (val >= 1_000_000) return (val / 1_000_000).toFixed(1) + 'M';
  if (val >= 1_000) return (val / 1_000).toFixed(1) + 'K';
  return val.toString();
}

// ── Types ─────────────────────────────────────────────────────────────────────

type TabKey = 'spending' | 'efficiency' | 'operations';
type SortDir = 'asc' | 'desc';
type OpFilter = 'all' | 'failed' | 'expensive' | 'slow';

interface CostRecord {
  _id: string;
  operationType: string;
  modelName: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  inputCost: number;
  outputCost: number;
  totalCost: number;
  requestDuration: number;
  success: boolean;
  errorMessage: string;
  createdAt: string;
}

interface OperationsSummary {
  failedCount: number;
  failedCost: number;
  maxDuration: number;
  maxSingleCost: number;
}

interface ModelBreakdownEntry {
  requests: number;
  totalCost: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalLatencyMs: number;
  successCount: number;
}

interface CostAnalyticsData {
  totalCost: number;
  totalRequests: number;
  totalTokens: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  averageCostPerRequest: number;
  successCount: number;
  failedCount: number;
  failedCost: number;
  costByOperation: Record<string, number>;
  costByDay: Array<{ date: string; cost: number; requests: number; failures: number }>;
  costByModelName: Record<string, number>;
  modelBreakdown: Record<string, ModelBreakdownEntry>;
  /** Present only when some of these costs came through Chat External API keys */
  costByApiKey?: Array<{ apiKeyId: string; requests: number; cost: number; inputTokens: number; outputTokens: number }>;
}

interface CostSummary {
  totalCost: number;
  totalRequests: number;
  costToday: number;
  costThisMonth: number;
  previousMonths?: Array<{ month: string; label: string; cost: number; requests: number }>;
  topExpensiveOperations: CostRecord[];
  recentActivity: CostRecord[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

const opColorMap: Record<string, { bg: string; text: string }> = {
  chat:                  { bg: 'bg-indigo-50',  text: 'text-indigo-700'  },
  document_processing:   { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  summary_generation:    { bg: 'bg-violet-50',  text: 'text-violet-700'  },
  document_reprocessing: { bg: 'bg-slate-100',  text: 'text-slate-600'   },
  guardrail_validation:  { bg: 'bg-amber-50',   text: 'text-amber-700'   },
};

const opLabel: Record<string, string> = {
  chat:                  'Chat',
  document_processing:   'Document Processing',
  summary_generation:    'Summary Generation',
  document_reprocessing: 'Doc Reprocessing',
  guardrail_validation:  'Guardrail Validation',
};

const opColors: Record<string, string> = {
  chat:                  '#6366F1',
  document_processing:   '#10B981',
  summary_generation:    '#8B5CF6',
  document_reprocessing: '#64748B',
  guardrail_validation:  '#F59E0B',
};

// ── Chart tooltip ─────────────────────────────────────────────────────────────

function CostTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800 mb-1.5">{label}</p>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-start gap-1.5 mt-1">
          <span
            className="w-2 h-2 rounded-sm inline-block mt-0.5 flex-shrink-0"
            style={{ background: p.fill ?? p.color ?? '#6366F1' }}
          />
          <div>
            <span className="text-gray-500">{p.name}: </span>
            {p.name === 'Cost' ? (
              <>
                <span className="font-medium text-gray-800">{inrFromUsd(p.value)}</span>
                <span className="text-gray-400 ml-1">({usdStr(p.value)})</span>
              </>
            ) : (
              <span className="font-medium text-gray-800">{p.value}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const CostAnalytics: React.FC = () => {
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  const [overallAnalytics, setOverallAnalytics] = useState<CostAnalyticsData | null>(null);
  const [costRecords, setCostRecords] = useState<CostRecord[]>([]);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  // External API key filter (admin only; keys of the selected client)
  const [selectedApiKeyId, setSelectedApiKeyId] = useState('');
  const [apiKeys, setApiKeys] = useState<ExternalApiKeyView[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('spending');

  // Operations tab state
  const [opFilter, setOpFilter] = useState<OpFilter>('all');
  const [opSearch, setOpSearch] = useState('');
  const [sortCol, setSortCol] = useState<string>('totalCost');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Operations tab — date range & pagination
  const [opStartDate, setOpStartDate] = useState<string>(
    () => dayjs().subtract(24, 'hour').format('YYYY-MM-DDTHH:mm'),
  );
  const [opEndDate, setOpEndDate] = useState<string>(
    () => dayjs().format('YYYY-MM-DDTHH:mm'),
  );
  const [opPage, setOpPage] = useState(1);
  const opPageSize = 20;
  const [opTotal, setOpTotal] = useState(0);
  const [opPages, setOpPages] = useState(0);
  const [opLoading, setOpLoading] = useState(false);
  const [opSummary, setOpSummary] = useState<OperationsSummary>({
    failedCount: 0,
    failedCost: 0,
    maxDuration: 0,
    maxSingleCost: 0,
  });

  useEffect(() => {
    if (isAdmin) fetchClients();
    fetchEvents();
    fetchCostData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // API keys of the selected client (admin only) for the "by API key" filter
  useEffect(() => {
    setSelectedApiKeyId('');
    setApiKeys([]);
    if (!isAdmin || !selectedClientId) return;
    externalApiKeyApi.listForClient(selectedClientId)
      .then(res => setApiKeys(res.data.data?.keys || []))
      .catch(() => { /* filter is optional; keep the page usable */ });
  }, [isAdmin, selectedClientId]);

  useEffect(() => {
    fetchCostData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedEventId, selectedApiKeyId]);

  // Re-fetch operations when tab is activated or client/event changes
  useEffect(() => {
    if (activeTab === 'operations') fetchOperations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedClientId, selectedEventId, selectedApiKeyId]);

  // Re-fetch when filter changes
  useEffect(() => {
    if (activeTab === 'operations') fetchOperations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opFilter]);

  // Debounced re-fetch when search changes
  useEffect(() => {
    if (activeTab !== 'operations') return;
    const timer = setTimeout(() => fetchOperations(1), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opSearch]);

  const fetchClients = async () => {
    try {
      const response = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (response.data.success && response.data.data) {
        setClients(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const params: any = { limit: 100 };
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const response = await eventsApi.getAll(params);
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchCostData = async () => {
    setLoading(true);
    setCostSummary(null);
    setOverallAnalytics(null);
    try {
      const params: any = {};
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      if (selectedEventId) params.eventId = selectedEventId;
      if (isAdmin && selectedApiKeyId) params.apiKeyId = selectedApiKeyId;

      const [summaryRes, analyticsRes] = await Promise.all([
        apiService.get('/costs/summary', { params }),
        apiService.get('/costs/analytics/overall', { params }),
      ]);

      if (summaryRes.success) setCostSummary(summaryRes.data);
      if (analyticsRes.success) setOverallAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching cost data:', error);
      toast.error('Failed to load cost analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperations = useCallback(async (page = 1) => {
    const startDate = new Date(opStartDate);
    const endDate = new Date(opEndDate);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      toast.error('Please select a valid date range');
      return;
    }
    if (startDate > endDate) {
      toast.error('From date cannot be after To date');
      return;
    }

    setOpLoading(true);
    try {
      const params: any = {
        page,
        limit: opPageSize,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      if (selectedEventId) params.eventId = selectedEventId;
      if (isAdmin && selectedApiKeyId) params.apiKeyId = selectedApiKeyId;
      if (opSearch.trim()) params.search = opSearch.trim();
      if (opFilter === 'failed') params.success = 'false';
      if (opFilter === 'expensive' || opFilter === 'slow') params.opFilter = opFilter;

      const res = await apiService.get('/costs/operations', { params });
      if (res.success) {
        setCostRecords(res.data.records || []);
        setOpTotal(res.data.pagination?.total ?? 0);
        setOpPages(res.data.pagination?.pages ?? 0);
        setOpSummary({
          failedCount: res.data.summary?.failedCount ?? 0,
          failedCost: res.data.summary?.failedCost ?? 0,
          maxDuration: res.data.summary?.maxDuration ?? 0,
          maxSingleCost: res.data.summary?.maxSingleCost ?? 0,
        });
        setOpPage(page);
      }
    } catch (err) {
      console.error('Failed to fetch operations:', err);
      toast.error('Failed to load operations');
    } finally {
      setOpLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opStartDate, opEndDate, opFilter, opSearch, opPageSize, selectedClientId, selectedEventId, selectedApiKeyId, isAdmin, user?._id]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const dailyCostData = useMemo(
    () =>
      (overallAnalytics?.costByDay ?? []).map(item => ({
        date: dayjs(item.date).format('MM/DD'),
        cost: item.cost,
        requests: item.requests,
      })),
    [overallAnalytics],
  );

  const operationBreakdown = useMemo(() => {
    const costByOp = overallAnalytics?.costByOperation ?? {};
    const total = Object.values(costByOp).reduce((a, b) => a + b, 0) || 1;
    return Object.entries(costByOp)
      .map(([op, cost]) => ({
        operation: opLabel[op] || op,
        opKey: op,
        cost: Number(cost),
        pct: (Number(cost) / total) * 100,
        color: opColors[op] || '#64748B',
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [overallAnalytics]);

  const modelEfficiency = useMemo(() => {
    const breakdown = overallAnalytics?.modelBreakdown ?? {};
    return Object.entries(breakdown)
      .map(([model, data]) => ({
        model,
        requests: data.requests,
        totalCost: data.totalCost,
        avgLatency: data.requests > 0 ? Math.round(data.totalLatencyMs / data.requests) : 0,
        successRate: data.requests > 0 ? (data.successCount / data.requests) * 100 : 0,
        costPer1kTokens:
          data.totalInputTokens + data.totalOutputTokens > 0
            ? (data.totalCost / (data.totalInputTokens + data.totalOutputTokens)) * 1000
            : 0,
      }))
      .sort((a, b) => b.totalCost - a.totalCost);
  }, [overallAnalytics]);

  const tokenBreakdown = useMemo(() => {
    const totalInput = overallAnalytics?.totalInputTokens ?? 0;
    const totalOutput = overallAnalytics?.totalOutputTokens ?? 0;
    const total = totalInput + totalOutput || 1;
    const totalRequests = overallAnalytics?.totalRequests ?? 0;
    return {
      totalInput,
      totalOutput,
      inputPct: parseFloat(((totalInput / total) * 100).toFixed(1)),
      outputPct: parseFloat(((totalOutput / total) * 100).toFixed(1)),
      avgInputPerReq: totalRequests > 0 ? Math.round(totalInput / totalRequests) : 0,
      avgOutputPerReq: totalRequests > 0 ? Math.round(totalOutput / totalRequests) : 0,
    };
  }, [overallAnalytics]);

  // Use backend-aggregated values (accurate over all records, not just the 100-record sample)
  const failedCount = overallAnalytics?.failedCount ?? 0;
  const failedCost = overallAnalytics?.failedCost ?? 0;
  const successRate =
    overallAnalytics && overallAnalytics.totalRequests > 0
      ? parseFloat(((overallAnalytics.successCount / overallAnalytics.totalRequests) * 100).toFixed(1))
      : 0;

  // All filtering is done backend; only apply sort on the current page
  const filteredRecords = useMemo(() => {
    return [...costRecords].sort((a: any, b: any) => {
      const av = a[sortCol];
      const bv = b[sortCol];
      if (typeof av === 'number' && typeof bv === 'number')
        return sortDir === 'desc' ? bv - av : av - bv;
      return sortDir === 'desc'
        ? String(bv).localeCompare(String(av))
        : String(av).localeCompare(String(bv));
    });
  }, [costRecords, sortCol, sortDir]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortCol(col); setSortDir('desc'); }
  };

  const cheapestModel =
    modelEfficiency.length > 0
      ? modelEfficiency.reduce((a, b) =>
          (a.costPer1kTokens || Infinity) < (b.costPer1kTokens || Infinity) ? a : b,
        )
      : null;
  const priesiestModel =
    modelEfficiency.length > 1
      ? modelEfficiency.reduce((a, b) =>
          (a.costPer1kTokens || 0) > (b.costPer1kTokens || 0) ? a : b,
        )
      : null;

  const ioRatio =
    tokenBreakdown.outputPct > 0
      ? (tokenBreakdown.inputPct / tokenBreakdown.outputPct).toFixed(1)
      : '—';

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'spending', label: 'Spending Overview' },
    { key: 'efficiency', label: 'Efficiency Insights' },
    { key: 'operations', label: 'Top Operations' },
  ];

  const opTableCols = [
    { key: 'createdAt', label: 'Time', width: '10%' },
    { key: 'operationType', label: 'Operation', width: '18%' },
    { key: 'modelName', label: 'Model', width: '16%' },
    { key: 'totalTokens', label: 'Tokens', width: '12%' },
    { key: 'totalCost', label: 'Cost', width: '12%' },
    { key: 'requestDuration', label: 'Duration', width: '9%' },
    { key: 'success', label: 'Status', width: '9%' },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">AI Cost Analytics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {selectedEventId
              ? events.find(e => (e._id || e.eventId) === selectedEventId)?.name ?? 'Selected Event'
              : selectedClientId
              ? `All knowledge assistants for ${clients.find(c => c._id === selectedClientId)?.name ?? 'selected client'}`
              : 'All knowledge assistants'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <select
              value={selectedClientId}
              onChange={e => {
                setSelectedClientId(e.target.value);
                setSelectedEventId('');
              }}
              className="h-9 px-3 border border-border rounded-lg text-sm bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>
                  {c.name} ({c.email})
                </option>
              ))}
            </select>
          )}
          <select
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            className="h-9 px-3 border border-border rounded-lg text-sm bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Knowledge Assistants</option>
            {events.map(e => (
              <option key={e._id || e.eventId} value={e._id || e.eventId}>
                {e.name}
              </option>
            ))}
          </select>
          {isAdmin && selectedClientId && apiKeys.length > 0 && (
            <select
              value={selectedApiKeyId}
              onChange={e => setSelectedApiKeyId(e.target.value)}
              aria-label="Filter by API key"
              className="h-9 px-3 border border-border rounded-lg text-sm bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All traffic (incl. all API keys)</option>
              {apiKeys.map(k => (
                <option key={k._id} value={k._id}>API key: {k.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => {
              fetchCostData();
              if (activeTab === 'operations') fetchOperations(opPage);
            }}
            disabled={loading}
            className="h-9 px-3 flex items-center gap-1.5 border border-border rounded-lg text-sm bg-white text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => toast.info('Export coming soon')}
            className="h-9 px-3 flex items-center gap-1.5 border border-border rounded-lg text-sm bg-white text-foreground hover:bg-accent transition-colors"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          Loading analytics…
        </div>
      )}

      {/* ── Tab: Spending Overview ────────────────────────── */}
      {!loading && activeTab === 'spending' && (
        <div className="space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Spending overview</h2>
            <p className="text-xs text-gray-500 mt-0.5">Total AI costs for this period</p>
          </div>

          {/* Summary cards */}
          <div className="flex gap-3 flex-wrap">
            {/* Total Spend */}
            <div className="flex-1 min-w-0 rounded-xl p-4 bg-indigo-50 relative">
              <div className="absolute top-0 right-1 text-5xl opacity-[0.07] font-bold select-none text-indigo-700">₹</div>
              <div className="text-xs font-medium uppercase tracking-wide mb-2 text-indigo-700 opacity-80">Total Spend</div>
              <div className="text-2xl font-semibold leading-none text-indigo-700">
                {inrFromUsd(costSummary?.totalCost ?? 0)}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{usdStr(costSummary?.totalCost ?? 0)} USD</div>
            </div>
            {/* Total Requests */}
            <div className="flex-1 min-w-0 rounded-xl p-4 bg-sky-50 relative">
              <div className="absolute top-0 right-1 text-5xl opacity-[0.07] font-bold select-none text-sky-700">#</div>
              <div className="text-xs font-medium uppercase tracking-wide mb-2 text-sky-700 opacity-80">Total Requests</div>
              <div className="text-3xl font-semibold leading-none text-sky-700">
                {(costSummary?.totalRequests ?? 0).toLocaleString()}
              </div>
              <div className="text-xs mt-1 text-sky-700 opacity-60">
                {formatTokens(overallAnalytics?.totalTokens ?? 0)} tokens processed
              </div>
            </div>
            {/* Avg Cost / Request */}
            <div className="flex-1 min-w-0 rounded-xl p-4 bg-emerald-50 relative">
              <div className="absolute top-0 right-1 text-5xl opacity-[0.07] font-bold select-none text-emerald-700">~</div>
              <div className="text-xs font-medium uppercase tracking-wide mb-2 text-emerald-700 opacity-80">Avg Cost / Request</div>
              <div className="text-2xl font-semibold leading-none text-emerald-700">
                {inrFromUsd(overallAnalytics?.averageCostPerRequest ?? 0)}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{usdStr(overallAnalytics?.averageCostPerRequest ?? 0)} USD</div>
            </div>
            {/* Failed Requests */}
            <div
              className={`flex-1 min-w-0 rounded-xl p-4 relative ${
                failedCount > 5 ? 'bg-rose-50' : failedCount > 0 ? 'bg-amber-50' : 'bg-emerald-50'
              }`}
            >
              <div
                className={`absolute top-0 right-1 text-5xl opacity-[0.07] font-bold select-none ${
                  failedCount > 5 ? 'text-rose-700' : failedCount > 0 ? 'text-amber-700' : 'text-emerald-700'
                }`}
              >
                !
              </div>
              <div
                className={`text-xs font-medium uppercase tracking-wide mb-2 opacity-80 ${
                  failedCount > 5 ? 'text-rose-700' : failedCount > 0 ? 'text-amber-700' : 'text-emerald-700'
                }`}
              >
                Failed Requests
              </div>
              <div
                className={`text-3xl font-semibold leading-none ${
                  failedCount > 5 ? 'text-rose-700' : failedCount > 0 ? 'text-amber-700' : 'text-emerald-700'
                }`}
              >
                {failedCount}
              </div>
              <div className={`text-xs mt-1 ${failedCost > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                {inrFromUsd(failedCost)} wasted
              </div>
            </div>
            {/* Success Rate */}
            <div
              className={`flex-1 min-w-0 rounded-xl p-4 relative ${
                successRate >= 98 ? 'bg-emerald-50' : successRate >= 90 ? 'bg-amber-50' : 'bg-rose-50'
              }`}
            >
              <div
                className={`absolute top-0 right-1 text-5xl opacity-[0.07] font-bold select-none ${
                  successRate >= 98 ? 'text-emerald-700' : successRate >= 90 ? 'text-amber-700' : 'text-rose-700'
                }`}
              >
                ✓
              </div>
              <div
                className={`text-xs font-medium uppercase tracking-wide mb-2 opacity-80 ${
                  successRate >= 98 ? 'text-emerald-700' : successRate >= 90 ? 'text-amber-700' : 'text-rose-700'
                }`}
              >
                Success Rate
              </div>
              <div
                className={`text-3xl font-semibold leading-none ${
                  successRate >= 98 ? 'text-emerald-700' : successRate >= 90 ? 'text-amber-700' : 'text-rose-700'
                }`}
              >
                {successRate}%
              </div>
              <div
                className={`text-xs mt-1 ${
                  successRate >= 98 ? 'text-emerald-600' : successRate >= 90 ? 'text-amber-600' : 'text-rose-600'
                }`}
              >
                {successRate >= 98 ? 'Excellent' : successRate >= 90 ? 'Acceptable — monitor' : 'Investigate failures'}
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-[1.6fr_1fr] gap-5">
            {/* Daily cost trend */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Daily cost trend</h3>
              <p className="text-xs text-gray-500 mb-4">Spend over time with request volume overlay</p>
              <div className="flex items-center gap-5 mb-3 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm bg-indigo-500 inline-block" />Cost
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-6 inline-block border-t-2 border-dashed border-slate-400" />Requests
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-400 inline-block" />Failures
                </span>
              </div>
              {dailyCostData.length > 0 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <ComposedChart data={dailyCostData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                    <YAxis
                      yAxisId="cost"
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={inrAxisTick}
                      width={46}
                    />
                    <YAxis
                      yAxisId="requests"
                      orientation="right"
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      width={28}
                    />
                    <Tooltip content={(props: any) => <CostTooltip {...props} />} />
                    <Area
                      yAxisId="cost"
                      type="monotone"
                      dataKey="cost"
                      name="Cost"
                      stroke="#6366F1"
                      fill="url(#costGrad)"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="requests"
                      type="monotone"
                      dataKey="requests"
                      name="Requests"
                      stroke="#64748B"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                    />
                    <Bar
                      yAxisId="requests"
                      dataKey="failures"
                      name="Failures"
                      fill="#F87171"
                      radius={[2, 2, 0, 0]}
                      maxBarSize={8}
                      opacity={0.85}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[230px] text-sm text-gray-400">No cost data</div>
              )}
            </div>

            {/* Cost by operation */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Cost by operation</h3>
              <p className="text-xs text-gray-500 mb-4">Where your budget goes</p>
              {operationBreakdown.length > 0 ? (
                <>
                  <div className="space-y-4">
                    {operationBreakdown.map(op => (
                      <div key={op.opKey}>
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="font-medium text-gray-800">{op.operation}</span>
                          <span className="text-gray-500">{inrFromUsd(op.cost)}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${op.pct}%`, background: op.color }}
                          />
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5 text-right">{op.pct.toFixed(1)}%</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex h-1.5 rounded-full overflow-hidden mt-4">
                    {operationBreakdown.map(op => (
                      <div key={op.opKey} style={{ width: `${op.pct}%`, background: op.color }} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-sm text-gray-400">No data</div>
              )}
            </div>
          </div>

          {/* Today / This Month / Prior 3 months */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Cost Today</div>
              <div className="text-2xl font-semibold text-gray-900">{inrFromUsd(costSummary?.costToday ?? 0)}</div>
              <div className="text-xs text-gray-400 mt-0.5">{usdStr(costSummary?.costToday ?? 0)} USD</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                Cost This Month ({dayjs().format('MMMM')})
              </div>
              <div className="text-2xl font-semibold text-gray-900">{inrFromUsd(costSummary?.costThisMonth ?? 0)}</div>
              <div className="text-xs text-gray-400 mt-0.5">{usdStr(costSummary?.costThisMonth ?? 0)} USD</div>
            </div>
            {(costSummary?.previousMonths ?? []).map(m => (
              <div key={m.month} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">
                  Cost {m.label}
                </div>
                <div className="text-2xl font-semibold text-gray-900">{inrFromUsd(m.cost)}</div>
                <div className="text-xs text-gray-400 mt-0.5">{usdStr(m.cost)} USD</div>
              </div>
            ))}
          </div>

          {/* TODO: Make This AI-driven */}
          {/* Insights and recommendations — currently rule-based, replace with AI-generated insights */}
          {/* {overallAnalytics && (() => {
            const insights: { icon: string; text: string }[] = [];
            if (cheapestModel && priesiestModel && cheapestModel.model !== priesiestModel.model && priesiestModel.costPer1kTokens > 0) {
              const savingsPct = ((priesiestModel.costPer1kTokens / cheapestModel.costPer1kTokens - 1) * 100).toFixed(0);
              insights.push({ icon: '₹', text: `Switch heavy operations to ${cheapestModel.model} to reduce per-token cost by ${savingsPct}%` });
            }
            if (failedCount > 0) {
              insights.push({ icon: '!', text: `${failedCount} failed request${failedCount > 1 ? 's' : ''} wasted ${inrFromUsd(failedCost)} (${usdStr(failedCost)} USD) — check error details in the Top Operations tab` });
            }
            if (tokenBreakdown.totalInput + tokenBreakdown.totalOutput > 0) {
              insights.push({ icon: '#', text: `Input tokens are ${tokenBreakdown.inputPct}% of total — review prompt length and RAG chunk count (ragTopK) to reduce context size` });
            }
            const spikeDayEntry = dailyCostData.length > 0 ? dailyCostData.reduce((a, b) => a.cost > b.cost ? a : b) : null;
            if (spikeDayEntry && overallAnalytics.totalCost > 0 && spikeDayEntry.cost > 0) {
              const spikePct = ((spikeDayEntry.cost / overallAnalytics.totalCost) * 100).toFixed(0);
              insights.push({ icon: '^', text: `Spike on ${spikeDayEntry.date} (${inrFromUsd(spikeDayEntry.cost)}) was ${spikePct}% of total spend — investigate activity on that date` });
            }
            if (insights.length === 0) return null;
            return (
              <div className="bg-indigo-50 rounded-xl p-5">
                <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700 mb-4">
                  <Lightbulb className="w-4 h-4" />
                  Insights and recommendations
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {insights.map((ins, i) => (
                    <div key={i} className="flex gap-2.5 text-xs text-indigo-700 leading-relaxed">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-200/60 inline-flex items-center justify-center font-bold text-[10px]">
                        {ins.icon}
                      </span>
                      <span>{ins.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()} */}

          {/* Spend by API key (Chat External) */}
          {overallAnalytics?.costByApiKey && overallAnalytics.costByApiKey.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900">Spend by API key</h3>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">AI calls made through Chat External, grouped by the key that made them</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="py-1 pr-3 font-medium">API key</th>
                    <th className="py-1 pr-3 font-medium">AI calls</th>
                    <th className="py-1 pr-3 font-medium">Tokens in / out</th>
                    <th className="py-1 font-medium text-right">Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {overallAnalytics.costByApiKey.map(row => (
                    <tr key={row.apiKeyId}>
                      <td className="py-1.5 pr-3 text-gray-900">{apiKeys.find(k => k._id === row.apiKeyId)?.name ?? `Key …${row.apiKeyId.slice(-6)}`}</td>
                      <td className="py-1.5 pr-3 text-gray-600">{row.requests.toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-gray-600">{row.inputTokens.toLocaleString()} / {row.outputTokens.toLocaleString()}</td>
                      <td className="py-1.5 text-right text-gray-900">{inrFromUsd(row.cost)} <span className="text-xs text-gray-400">{usdStr(row.cost)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Efficiency Insights ──────────────────────── */}
      {!loading && activeTab === 'efficiency' && (
        <div className="space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Efficiency insights</h2>
            <p className="text-xs text-gray-500 mt-0.5">Compare AI models and monitor token usage</p>
          </div>

          <div className="grid grid-cols-[1.15fr_1fr] gap-5">
            {/* Model efficiency */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Model efficiency</h3>
              <p className="text-xs text-gray-500 mb-4">Cost, speed, and reliability across models used</p>
              {modelEfficiency.length === 0 ? (
                <div className="text-sm text-gray-400 py-8 text-center">No model data available</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {modelEfficiency.map(m => {
                    const isCheapest = cheapestModel?.model === m.model;
                    const isPriciest = priesiestModel?.model === m.model;
                    return (
                      <div key={m.model} className="py-4 first:pt-0 last:pb-0">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-gray-800">{m.model}</span>
                            {isCheapest && (
                              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase tracking-wide">
                                Cheapest
                              </span>
                            )}
                            {isPriciest && !isCheapest && (
                              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 uppercase tracking-wide">
                                Priciest
                              </span>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xs font-semibold text-indigo-600">{inrFromUsd(m.totalCost)}</div>
                            <div className="text-[10px] text-gray-400">{usdStr(m.totalCost)} USD</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          {[
                            { label: 'Requests', val: String(m.requests), sub: null, colorClass: 'text-gray-900' },
                            {
                              label: '₹/1K tokens',
                              val: inrFromUsd(m.costPer1kTokens),
                              sub: usdStr(m.costPer1kTokens),
                              colorClass: 'text-gray-900',
                            },
                            { label: 'Avg latency', val: m.avgLatency + 'ms', sub: null, colorClass: 'text-gray-900' },
                            {
                              label: 'Success',
                              val: m.successRate.toFixed(1) + '%',
                              sub: null,
                              colorClass:
                                m.successRate >= 98
                                  ? 'text-emerald-600'
                                  : m.successRate >= 90
                                  ? 'text-amber-600'
                                  : 'text-rose-600',
                            },
                          ].map((stat, si) => (
                            <div key={si}>
                              <div className="text-[10px] text-gray-400">{stat.label}</div>
                              <div className={`text-xs font-semibold mt-0.5 ${stat.colorClass}`}>{stat.val}</div>
                              {stat.sub && <div className="text-[9px] text-gray-400">{stat.sub}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Token breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">Token breakdown</h3>
              <p className="text-xs text-gray-500 mb-4">Input vs. output token distribution</p>
              {tokenBreakdown.totalInput + tokenBreakdown.totalOutput > 0 ? (
                <>
                  <div className="flex h-8 rounded-lg overflow-hidden mb-4">
                    <div
                      className="flex items-center justify-center"
                      style={{ width: `${tokenBreakdown.inputPct}%`, background: '#0EA5E9' }}
                    >
                      <span className="text-[10px] font-semibold text-white">
                        Input {tokenBreakdown.inputPct}%
                      </span>
                    </div>
                    <div
                      className="flex items-center justify-center"
                      style={{ width: `${tokenBreakdown.outputPct}%`, background: '#8B5CF6' }}
                    >
                      <span className="text-[10px] font-semibold text-white">
                        Out {tokenBreakdown.outputPct}%
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {[
                      {
                        label: 'Input tokens',
                        val: formatTokens(tokenBreakdown.totalInput),
                        bg: 'bg-sky-50',
                        text: 'text-sky-700',
                      },
                      {
                        label: 'Output tokens',
                        val: formatTokens(tokenBreakdown.totalOutput),
                        bg: 'bg-violet-50',
                        text: 'text-violet-700',
                      },
                      {
                        label: 'Avg input / req',
                        val: tokenBreakdown.avgInputPerReq.toLocaleString(),
                        bg: 'bg-sky-50',
                        text: 'text-sky-700',
                      },
                      {
                        label: 'Avg output / req',
                        val: tokenBreakdown.avgOutputPerReq.toLocaleString(),
                        bg: 'bg-violet-50',
                        text: 'text-violet-700',
                      },
                    ].map((s, i) => (
                      <div key={i} className={`${s.bg} rounded-lg p-3`}>
                        <div className={`text-[10px] font-medium uppercase tracking-wide ${s.text} opacity-70`}>
                          {s.label}
                        </div>
                        <div className={`text-xl font-semibold mt-0.5 ${s.text}`}>{s.val}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 pt-3 border-t border-gray-100">
                    Input:Output ratio is <strong className="text-sky-600">{ioRatio}:1</strong> —{' '}
                    {tokenBreakdown.inputPct > 70
                      ? 'prompts are heavy, consider trimming context'
                      : 'balanced ratio'}
                  </p>
                  {overallAnalytics && overallAnalytics.totalCost > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-2.5">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Total token cost</p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-sky-400 inline-block" />Input tokens
                        </span>
                        <span className="font-medium text-gray-800">
                          {inrFromUsd(overallAnalytics.totalCost * (tokenBreakdown.inputPct / 100))}
                          <span className="text-gray-400 ml-1 font-normal">({usdStr(overallAnalytics.totalCost * (tokenBreakdown.inputPct / 100))})</span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-violet-400 inline-block" />Output tokens
                        </span>
                        <span className="font-medium text-gray-800">
                          {inrFromUsd(overallAnalytics.totalCost * (tokenBreakdown.outputPct / 100))}
                          <span className="text-gray-400 ml-1 font-normal">({usdStr(overallAnalytics.totalCost * (tokenBreakdown.outputPct / 100))})</span>
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-gray-400 py-8 text-center">No token data available</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Top Operations ───────────────────────────── */}
      {!loading && activeTab === 'operations' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Operations log</h2>
            <p className="text-xs text-gray-500 mt-0.5">Detailed AI operations with cost and performance data</p>
          </div>

          {/* Stat cards — moved to top */}
          {opTotal > 0 && (() => {
            const maxDur = opSummary.maxDuration;
            const maxSingleCost = opSummary.maxSingleCost;
            return (
              <div className="flex gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px] bg-rose-50 rounded-xl p-4">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-rose-700 opacity-70 mb-1">Failed requests cost</div>
                  <div className="text-2xl font-semibold text-rose-700">{inrFromUsd(opSummary.failedCost)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{usdStr(opSummary.failedCost)} USD</div>
                  <div className="text-xs mt-1 text-rose-700 opacity-60">Tokens consumed before failure</div>
                </div>
                <div className="flex-1 min-w-[200px] bg-amber-50 rounded-xl p-4">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-amber-700 opacity-70 mb-1">Slowest operation</div>
                  <div className="text-2xl font-semibold text-amber-700">
                    {maxDur >= 1000 ? (maxDur / 1000).toFixed(1) + 's' : maxDur + 'ms'}
                  </div>
                  <div className="text-xs mt-1 text-amber-700 opacity-60">Consider chunking large documents</div>
                </div>
                <div className="flex-1 min-w-[200px] bg-indigo-50 rounded-xl p-4">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-indigo-700 opacity-70 mb-1">Most expensive single request</div>
                  <div className="text-2xl font-semibold text-indigo-700">{inrFromUsd(maxSingleCost)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{usdStr(maxSingleCost)} USD</div>
                </div>
              </div>
            );
          })()}

          {/* Date range filter */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-gray-500 font-medium">From</span>
            <input
              type="datetime-local"
              value={opStartDate}
              onChange={e => setOpStartDate(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-xs text-gray-500 font-medium">To</span>
            <input
              type="datetime-local"
              value={opEndDate}
              onChange={e => setOpEndDate(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => fetchOperations(1)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              Apply
            </button>
          </div>

          {/* Search + filter buttons */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="relative min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search operation or model…"
                value={opSearch}
                onChange={e => setOpSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="w-px h-6 bg-gray-200" />
            {(
              [
                { key: 'all' as const,       label: 'All',       count: opTotal },
                { key: 'failed' as const,    label: 'Failed',    count: opSummary.failedCount },
                { key: 'expensive' as const, label: 'Expensive', count: null },
                { key: 'slow' as const,      label: 'Slow',      count: null },
              ]
            ).map(f => (
              <button
                key={f.key}
                onClick={() => setOpFilter(f.key)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                  opFilter === f.key
                    ? 'bg-indigo-50 border-indigo-400 text-indigo-700'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                {f.label}{f.count !== null && <span className="opacity-60"> ({f.count})</span>}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {opTableCols.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{ width: col.width }}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-100 cursor-pointer select-none hover:text-gray-600 transition-colors"
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortCol === col.key ? (
                          sortDir === 'desc' ? (
                            <ChevronDown className="w-3 h-3 text-indigo-500" />
                          ) : (
                            <ChevronUp className="w-3 h-3 text-indigo-500" />
                          )
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {opLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                      No operations found
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((r, i) => {
                    const opC = opColorMap[r.operationType] ?? { bg: 'bg-gray-100', text: 'text-gray-600' };
                    return (
                      <tr key={r._id || i} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${!r.success ? 'bg-rose-50/30' : ''}`}>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {dayjs(r.createdAt).format('MM/DD HH:mm')}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${opC.bg} ${opC.text}`}
                          >
                            {opLabel[r.operationType] || r.operationType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700 font-medium">{r.modelName}</td>
                        <td className="px-4 py-3 text-xs text-gray-700">
                          <div>{formatTokens(r.totalTokens)}</div>
                          <div className="text-[10px] text-gray-400">
                            {formatTokens(r.inputTokens)}↑ {formatTokens(r.outputTokens)}↓
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="font-medium text-gray-900">{inrFromUsd(r.totalCost)}</div>
                          <div className="text-[10px] text-gray-400">{usdStr(r.totalCost)}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {r.requestDuration >= 1000
                            ? (r.requestDuration / 1000).toFixed(1) + 's'
                            : r.requestDuration + 'ms'}
                        </td>
                        <td className="px-4 py-3">
                          {r.success ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700">
                              ✓ OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md bg-rose-50 text-rose-700">
                              ✗ Failed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {opPages > 1 && (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{opTotal} total · page {opPage} of {opPages}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => fetchOperations(opPage - 1)}
                  disabled={opPage <= 1 || opLoading}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>
                {Array.from({ length: Math.min(opPages, 5) }, (_, i) => {
                  const start = Math.max(1, opPage - 2);
                  const page = start + i;
                  if (page > opPages) return null;
                  return (
                    <button
                      key={page}
                      onClick={() => fetchOperations(page)}
                      disabled={opLoading}
                      className={`w-8 h-8 rounded-lg border transition-colors ${
                        page === opPage
                          ? 'bg-indigo-50 border-indigo-400 text-indigo-700 font-medium'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => fetchOperations(opPage + 1)}
                  disabled={opPage >= opPages || opLoading}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CostAnalytics;
